<div class="notification-wrapper" #notificationWrapper>
  <button type="button" class="action-btn notification-icon" (click)="toggleNotification()"
    [attr.aria-label]="unreadCount > 0 ? 'Notifications, ' + badgeLabel + ' unread' : 'Notifications'"
    [attr.aria-expanded]="isPanelOpen">
    <i class="hgi hgi-stroke hgi-rounded hgi-notification-01 bell"></i>
    @if (unreadCount > 0) {
    <span class="notification-count nav-badge">{{ badgeLabel }}</span>
    }
  </button>

  @if (isPanelOpen) {
  <dialog class="notification-panel" open aria-label="Notifications">
    <div class="panel-header">
      <div class="panel-heading">
        <h5 class="panel-title title-xs m-0">Notifications</h5>
        <label class="visually-hidden" for="notification-timeframe">Timeframe</label>
        <select id="notification-timeframe" class="timeframe-select caption-sm" [value]="timeframe"
          (change)="selectTimeframe($any($event.target).value)">
          @for (option of timeframeOptions; track option.value) {
          <option [value]="option.value" [selected]="option.value === timeframe">{{ option.label }}</option>
          }
        </select>
        @if (rows.length) {
        <a class="clear-all caption-sm" (click)="clearAllNotifications()">Clear All</a>
        }
      </div>
      <button type="button" class="close-icon" aria-label="Close notifications" (click)="closePanel()">&times;</button>
    </div>

    <div class="panel-toolbar">
      <div class="tabs" role="tablist">
        <button type="button" role="tab" class="tab" [class.active]="activeTab === 'all'"
          [attr.aria-selected]="activeTab === 'all'" (click)="selectTab('all')">All</button>
        <button type="button" role="tab" class="tab" [class.active]="activeTab === 'unread'"
          [attr.aria-selected]="activeTab === 'unread'" (click)="selectTab('unread')">
          Unread
          @if (unreadCount > 0) {
          <span class="tab-count">{{ badgeLabel }}</span>
          }
        </button>
        @if (isApprover) {
        <button type="button" role="tab" class="tab" [class.active]="activeTab === 'inbox'"
          [attr.aria-selected]="activeTab === 'inbox'" (click)="selectTab('inbox')">Inbox</button>
        }
      </div>
      <button type="button" class="mark-all caption-sm" [disabled]="unreadInView === 0 || markAllBusy || loading"
        (click)="markAllAsRead()">
        Mark all as read
      </button>
    </div>

    <div class="notification-list">
      @for (group of groups; track group.label) {
      <div class="group-label caption-sm">{{ group.label }}</div>
      <ul class="m-0 p-0">
        @for (msg of group.rows; track msg._id) {
        <li class="m-0">
          <div class="notification-item" [class.unread]="!msg.isRead">
            <div class="avatar body-sm" aria-hidden="true">
              <i class="hgi hgi-stroke hgi-rounded hgi-shield-01"></i>
            </div>

            <div class="notification-body">
              @if (activeTab === 'inbox' && msg.inbox) {
              <span class="pending-chip caption-sm">{{ pendingSinceLabel(msg) }}</span>
              }
              <button type="button" class="notification-content" [disabled]="busyIds.has(msg._id)"
                (click)="openNotification(msg)">
                <span class="notification-text body-sm" [title]="msg.title">{{ title(msg) }}</span>
                @if (activeTab === 'inbox' && msg.inbox?.requesterName) {
                <span class="requester-name body-xs">{{ msg.inbox?.requesterName }}</span>
                }
                <span class="notification-sub body-xs" [title]="msg.message"
                  [class.is-failed]="bulkUploadTone(msg) === 'failed'"
                  [class.is-success]="bulkUploadTone(msg) === 'success'">{{ description(msg) }}</span>
                @if (activeTab === 'inbox' && msg.inbox) {
                <span class="notification-time caption-sm">Submitted {{ msg.inbox.submittedAt | date: dateTimeFormat
                  }}</span>
                } @else {
                <span class="notification-time caption-sm">{{ msg.createdAt | date: dateTimeFormat }}</span>
                }
              </button>
              @if (activeTab === 'inbox' && msg.inbox) {
              <div class="inbox-actions">
                @if (canDecide(msg)) {
                <button type="button" class="inbox-btn reject caption-sm" [disabled]="decisionSubmitting"
                  (click)="openDecision(msg, 'reject', $event)">Reject</button>
                <button type="button" class="inbox-btn approve caption-sm" [disabled]="decisionSubmitting"
                  (click)="openDecision(msg, 'approve', $event)">Approve</button>
                } @else {
                <button type="button" class="inbox-btn review caption-sm" [disabled]="busyIds.has(msg._id)"
                  (click)="openNotification(msg)">Review</button>
                }
              </div>
              }
              @if (isLegacyDownload(msg) && reportLink(msg); as report) {
              <button type="button" class="download-link body-xs" (click)="downloadNotificationFile(report, $event)">
                <i class="fa fa-download"></i> {{ msg.moduleType === 'POLICY_EXPORT' ? 'Download' : 'Download Report' }}
              </button>
              }
            </div>

            <div class="notification-actions">
              @if (!msg.isRead) {
              <span class="unread-dot" aria-label="Unread"></span>
              }
              <button type="button" class="read-toggle caption-sm" [disabled]="busyIds.has(msg._id)"
                [title]="msg.isRead ? 'Mark as unread' : 'Mark as read'" (click)="toggleRead(msg, $event)">
                {{ msg.isRead ? 'Mark as unread' : 'Mark as read' }}
              </button>
            </div>
          </div>
        </li>
        }
      </ul>
      }

      @if (loading) {
        <div class="panel-state caption-sm">Loading…</div>
      } @else if (loadError) {
        <div class="panel-state error caption-sm">
          {{ loadError }}
          <a (click)="selectTimeframe(timeframe)">Retry</a>
        </div>
      } @else if (!rows.length) {
        <div class="notification-empty">
          <div class="empty-icon">
            <i class="hgi hgi-stroke hgi-rounded hgi-notification-01"></i>
          </div>

          <h6>All caught up!</h6>

          <p>You have no new notifications</p>
        </div>
      } @else if (hasMore) {
        <button type="button" class="load-more caption-sm" (click)="loadMore()">Load more</button>
      }
    </div>
  </dialog>
  }

  <!-- Inside the wrapper so working in the modal does not count as an outside click that closes the tray. -->
  <app-approval-action-modal [isOpen]="!!decisionRow" [actionType]="decisionType" [loading]="decisionSubmitting"
    (confirm)="confirmDecision($event)" (close)="closeDecision()"></app-approval-action-modal>
</div>